import { SurfaceOptionIdentifiers } from '../Enums';
import type { EditorItem } from '../Reducers/Configurator/types';

export const SURFACE_GLOSS_FILTER_ID = 'surface-gloss';
export const SURFACE_GLOSS_GRADIENT_ID = 'surface-gloss-gradient';
export const SURFACE_GLOSS_MASK_ID = 'surface-gloss-mask';
export const SURFACE_GLOSS_MASK_ALPHA_FILTER_ID = 'surface-gloss-mask-alpha';
export const SURFACE_GLOSS_ITEMS_ID = 'surface-gloss-items';
export const SURFACE_GLOSS_WARP_FILTER_ID = 'surface-gloss-warp';
export const SURFACE_GLOSS_STREAK_GRADIENT_ID = 'surface-gloss-streak';

export const SURFACE_MATTE_GRADIENT_ID = 'surface-matte-gradient';
export const SURFACE_MATTE_FILTER_ID = 'surface-matte';
export const SURFACE_MATTE_MASK_ID = 'surface-matte-mask';
export const SURFACE_MATTE_MASK_ALPHA_FILTER_ID = 'surface-matte-mask-alpha';
export const SURFACE_MATTE_ITEMS_ID = 'surface-matte-items';
export const SURFACE_MATTE_WARP_FILTER_ID = 'surface-matte-warp';
export const SURFACE_MATTE_STREAK_GRADIENT_ID = 'surface-matte-streak';


/**
 * Fraction of the narrowest stroke used as the bevel radius. The visible
 * bevel band spans roughly 2× the radius (blur + offset), so 0.12 keeps
 * ~3/4 of the stroke in its base color at every zoom level.
 */
const BEVEL_STROKE_RATIO = 0.08;

/** Assumed stroke/height ratio while an item's stroke is still being measured. */
const FALLBACK_STROKE_HEIGHT_RATIO = 0.15;

/**
 * Screen-pixel bounds: below ~1px the bevel disappears when zoomed out;
 * the upper bound is a safety cap on the blur kernel for extreme zooms.
 */
const SURFACE_FILTER_MIN_RADIUS_PX = 1;
const SURFACE_FILTER_MAX_RADIUS_PX = 64;

export function isGlossSurface(identifier: string | null | undefined): boolean {
  return (identifier || '').toLowerCase() === SurfaceOptionIdentifiers.gloss;
}

export function isMatteSurface(identifier: string | null | undefined): boolean {
  return (identifier || '').toLowerCase() === SurfaceOptionIdentifiers.matte;
}
/**
 * Bevel radius in canvas units (millimeters), proportional to the narrowest
 * measured stroke so the bevel-to-stroke ratio stays constant across zoom
 * levels and across thin script fonts vs. solid logos. Falls back to a
 * height-based estimate per item until its measurement resolves.
 */
export function computeFilterBlurRadius(
  objects: Record<string, EditorItem>,
  pxPerMm: number,
  strokeWidthsM: Record<string, number>
): number {
  let minStroke = Infinity;

  Object.values(objects).forEach((item) => {
    const stroke =
      strokeWidthsM[item.id] ?? (item.height > 0 ? item.height * FALLBACK_STROKE_HEIGHT_RATIO : 0);
    if (stroke > 0 && stroke < minStroke) {
      minStroke = stroke;
    }
  });

  if (!Number.isFinite(minStroke)) return 0;

  const physicalRadius = minStroke * BEVEL_STROKE_RATIO;
  if (!(pxPerMm > 0)) return physicalRadius;

  return Math.min(
    Math.max(physicalRadius, SURFACE_FILTER_MIN_RADIUS_PX / pxPerMm),
    SURFACE_FILTER_MAX_RADIUS_PX / pxPerMm
  );
}
